import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Preloader from "../../components/Preloader";
import { DocumentServices } from "../../services/DocumentService";
import { TipoPostulacionService } from "../../services/TipoPostulacionService";
import { ClienteService } from "../../services/ClienteService";
import { Search } from "lucide-react";
import { PostulanteService } from "../../services/PostulanteService";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/AuthContext";

const UpdatePostulante = () => {
    const { token } = useContext(AuthContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const [form, setForm] = useState({
        tipoPostulacion: "",
        nombres: "",
        tipoDocumento: "",
        numeroDocumento: "",
        telefono: "",
        email: "",
        edad: "",
    });
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [tiposPostulacion, setTiposPostulacion] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState("");

    const cargarTipoDocumentos = async () => {
        try {
            const response = await DocumentServices.getDocuments(token);
            // Filtrar los que NO contienen "RUC"
            const documentosFiltrados = (response || []).filter(
                (doc) =>
                    doc.description && !doc.description.toUpperCase().includes("RUC")
            );

            setTiposDocumentos(documentosFiltrados);
        } catch (error) {
            console.error("Error al cargar tipos de Documentos:", error);
        }
    };

    const cargarTipoPostulacion = async () => {
        try {
            const tipos = await TipoPostulacionService.getTipoPostulacion(token);
            setTiposPostulacion(tipos || []);
        } catch (error) {
            console.error("Error al cargar tipos de Documentos:", error);
        }
    };

    const fetchPostulantes = async () => {
        setLoading(true);
        try {
            const postulanteData = await PostulanteService.fetchPostulanteById(token, id);

            // Inicializa el formulario con los datos recibidos
            setForm({
                tipoPostulacion: postulanteData.id_tipo_postulacion || '',
                nombres: postulanteData.nombre_referido || '',
                tipoDocumento: postulanteData.id_tipo_documento || '',
                numeroDocumento: postulanteData.numero_documento || '',
                telefono: postulanteData.telefono || '',
                email: postulanteData.email || '',
                edad: postulanteData.edad || ''
            });


            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error al obtener la venta:", error);
        }
    };

    useEffect(() => {
        cargarTipoDocumentos();
        cargarTipoPostulacion();
        fetchPostulantes();
    }, []);


    const handleBackToList = () => {
        navigate("/admin/reclutamiento"); // ajusta según tu ruta de listado
    };

    const buscarCliente = async () => {
        try {
            if (!form.numeroDocumento || form.numeroDocumento.trim() === "") {
                setErrors((prev) => ({
                    ...prev,
                    numeroDocumento: "Ingrese un número válido",
                }));
                return;
            }

            const cliente = await ClienteService.buscarPorDocumento({
                token,
                tipoDocumento: form.tipoDocumento,
                numeroDocumento: form.numeroDocumento,
            });

            if (form.tipoDocumento === "1" && cliente) {
                setForm((prev) => ({
                    ...prev,
                    nombres: cliente.nombres + " " + cliente.apellido_paterno + " " + cliente.apellido_materno || "",

                }));
            }

            setErrors((prev) => ({
                ...prev,
                numeroDocumento: null,
            }));
        } catch (err) {
            const mensaje = err.response?.data?.error || err.message || "Error al consultar el servicio";
            setErrors((prev) => ({
                ...prev,
                numeroDocumento: mensaje,
            }));
        }
    }

    const validateNumericInput = (value) => {
        return value.replace(/[^0-9]/g, "");
    };


    const handleNumericChange = (e) => {
        const { name, value } = e.target;
        const numericValue = validateNumericInput(value);
        setForm((prev) => ({
            ...prev,
            [name]: numericValue,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errores = validarFormulario();

        if (Object.keys(errores).length > 0) {
            setErrors(errores);
            return;
        }
        setLoading(true);
        try {
            const response = await PostulanteService.actualizarPostulante(token, id, form);

            if (response.success) {
                toast.success(response.message, {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored",
                });
                setForm({
                    tipoPostulacion: "",
                    nombres: "",
                    tipoDocumento: "",
                    numeroDocumento: "",
                    telefono: "",
                    email: "",
                    edad: "",
                });
            } else {
                if (response.message === 'Ya existe un postulante con ese documento') {
                    setErrors({ numeroDocumento: response.message });
                } else {
                    setError(response.message || 'No se pudo registrar la postulación');
                }
            }
        } catch (err) {
            setError("Error al actualizar el postulante.");
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "edad") {
            // Solo permitir números
            if (!/^\d*$/.test(value)) return;
        }

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validarFormulario = () => {
        const nuevosErrores = {};

        if (!form.tipoPostulacion) {
            nuevosErrores.tipoPostulacion = "El tipo de postulación es obligatorio";
        }

        if (!form.tipoDocumento) {
            nuevosErrores.tipoDocumento = "El tipo de documento es obligatorio";
        }

        if (form.numeroDocumento && !/^\d+$/.test(form.numeroDocumento)) {
            nuevosErrores.numeroDocumento = "Ingrese un número de documento válido";
        }

        if (!form.nombres || form.nombres.trim() === "") {
            nuevosErrores.nombres = "El nombre es obligatorio";
        }

        if (!form.telefono && !/^\d{7,15}$/.test(form.telefono)) {
            nuevosErrores.telefono = "Ingrese un teléfono válido";
        }

        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            nuevosErrores.email = "Correo electrónico inválido";
        }

        if (form.edad && !/^\d+$/.test(form.edad)) {
            nuevosErrores.edad = "Ingrese una edad válida (solo números)";
        } else if (parseInt(form.edad) < 18 || parseInt(form.edad) > 100) {
            nuevosErrores.edad = "La edad debe estar entre 18 y 100 años";
        }

        return nuevosErrores;
    };

    return (
        <div className="ventas-admin-content">
            {loading && <Preloader />}
            <div className="breadcrumb-container">
                <span className="breadcrumb-link" onClick={handleBackToList}>
                    ← Listado de Postulantes
                </span>
                <span className="breadcrumb-current"> / Actualizar Postulante</span>
            </div>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            <div className="dashboard-card nuevo-producto-card">
                <span className="nueva-venta-title"> Actualizar Postulante</span>
                <form className="nuevo-producto-form" onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h3 className="section-title">Datos del Postulante</h3>
                        <div className="form-grid">
                            <div className="">
                                <label htmlFor="tipoPostulacion">Tipo de Postulación*</label>
                                <select
                                    name="tipoPostulacion"
                                    value={form.tipoPostulacion}
                                    onChange={handleChange}
                                >
                                    <option value="">Selecciona tipo de postulación</option>
                                    {tiposPostulacion.map((tipo) => (
                                        <option key={tipo.id} value={tipo.id}>
                                            {tipo.descripcion}
                                        </option>
                                    ))}
                                </select>
                                {errors.tipoPostulacion && (
                                    <span className="error">{errors.tipoPostulacion}</span>
                                )}
                            </div>
                            <div className="">
                                <label htmlFor="tipoDocumento">Tipo de Documento </label>
                                <select
                                    name="tipoDocumento"
                                    value={form.tipoDocumento}
                                    onChange={handleChange}

                                >
                                    <option value="">Selecciona tipo de documento</option>
                                    {tiposDocumentos.map((tipo) => (
                                        <option key={tipo.id} value={tipo.id}>
                                            {tipo.description}
                                        </option>
                                    ))}
                                </select>
                                {errors.tipoDocumento && (
                                    <span className="error">{errors.tipoDocumento}</span>
                                )}
                            </div>
                        </div>
                        <div className="form-grid">
                            <div className="">
                                <label htmlFor="numeroDocumento">Número de Documento </label>
                                <div className="input-with-button">
                                    <input
                                        type="text"
                                        name="numeroDocumento"
                                        value={form.numeroDocumento}
                                        onChange={handleNumericChange}
                                        placeholder="Solo números"
                                    />
                                    {(form.tipoDocumento == "1") && (
                                        <button
                                            type="button"
                                            className="search-btn-documento"
                                            title="Buscar cliente"
                                            onClick={buscarCliente}
                                        >
                                            <Search size={20} color="#555" />
                                        </button>
                                    )}
                                </div>
                                {errors.numeroDocumento && (
                                    <span className="error">{errors.numeroDocumento}</span>
                                )}
                            </div>
                            <div className="">
                                <label htmlFor="nombres">Nombres*</label>
                                <input
                                    type="text"
                                    name="nombres"
                                    value={form.nombres}
                                    onChange={handleChange}
                                    placeholder="Nombres del Postulante"
                                />
                                {errors.nombres && <span className="error">{errors.nombres}</span>}
                            </div>
                        </div>
                        <div className="form-grid">
                            <div className="">
                                <label htmlFor="telefono">Teléfono* </label>
                                <input
                                    type="text"
                                    name="telefono"
                                    value={form.telefono}
                                    onChange={handleChange}
                                    placeholder="Teléfono"
                                />
                                {errors.telefono && (
                                    <span className="error">{errors.telefono}</span>
                                )}
                            </div>
                            <div className="">
                                <label htmlFor="email">Correo Electrónico</label>
                                <input
                                    type="text"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="Ingresa un Correo Electrónico"
                                />
                                {errors.email && (
                                    <span className="error">{errors.email}</span>
                                )}
                            </div>
                        </div>
                        <div className="form-grid">
                            <div className="">
                                <label htmlFor="edad">Edad </label>
                                <input
                                    type="text"
                                    name="edad"
                                    value={form.edad}
                                    onChange={handleChange}
                                    placeholder="Edad del Postulante"
                                />
                                {errors.edad && (
                                    <span className="error">{errors.edad}</span>
                                )}
                            </div>
                        </div>
                    </div>


                    <div className="form-actions">

                        <button
                            className="btn btn-primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? "Actualizando..." : " Actualizar Postulante"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdatePostulante;
